// DOM event wiring not handled by the controls pane: help panel + tabs. Panel /
// colorbar collapse + drag are owned by `setupPanelCollapse` in panels.ts
// (via the unified `setupPane` helper). Pure DOM — no THREE imports.
//
// Returns a Disposer (see ./types): every listener, the help-panel
// MutationObserver, and the drag/resize handles are collected so the help/tabs
// wiring tears down completely. Latent today (magviz has no full teardown), but
// the contract is what webpic's embedding + HMR rely on.

import { makeFreelyDraggable, makeResizableFromCorner, raisePane, installFreeResizeClamp } from './draggablePanels';
import type { Disposer } from './types';
import type { ViewerContext } from '../app/context';

export function setupListeners(_viewer: ViewerContext): Disposer {
    const offs: Disposer[] = [];

    try {
        const helpPanelEl = document.getElementById('help-panel');
        const tabButtons = helpPanelEl ? Array.from(helpPanelEl.querySelectorAll<HTMLElement>('.help-tab')) : [];
        const panels = helpPanelEl ? Array.from(helpPanelEl.querySelectorAll<HTMLElement>('.help-tabpanel')) : [];
        const activateTab = (id: string | null): void => {
            tabButtons.forEach((b) => {
                const active = b.getAttribute('data-tab') === id;
                b.classList.toggle('active', active);
                b.setAttribute('aria-selected', active ? 'true' : 'false');
            });
            panels.forEach((p) => {
                const show = p.getAttribute('data-tabpanel') === id;
                p.hidden = !show;
            });
        };
        tabButtons.forEach((btn) => {
            const onClick = (ev: Event): void => { ev.preventDefault(); activateTab(btn.getAttribute('data-tab')); };
            btn.addEventListener('click', onClick);
            offs.push(() => btn.removeEventListener('click', onClick));
        });
        activateTab('welcome');
    } catch { /* swallow */ }

    const helpPanel = document.getElementById('help-panel');
    const helpClose = document.getElementById('help-close');
    if (helpPanel && helpClose) {
        // Make the welcome / help modal draggable by its header. No snap
        // or edge chrome — just free positioning, viewport-clamped.
        const helpHeader = helpPanel.querySelector('.help-header') as HTMLElement | null;
        const helpResize = helpPanel.querySelector('.help-resize') as HTMLElement | null;

        // Double-click the header to toggle a near-fullscreen "maximize"; a
        // second double-click restores the pre-maximize geometry. A real drag
        // or resize drops the saved geometry (via the onCommit/onReset hooks,
        // which fire only on a genuine gesture — never on the double-click
        // itself) so the next double-click maximizes afresh.
        const MAXIMIZE_MARGIN_PX = 14;
        let maximized = false;
        let savedGeom: Record<'left' | 'top' | 'right' | 'bottom' | 'transform' | 'width' | 'height', string> | null = null;
        const cancelMaximize = (): void => {
            if (!maximized) return;
            maximized = false;
            savedGeom = null;
            helpPanel.classList.remove('maximized');
        };

        // Allow dragging the modal even when the gesture starts on a tab chip
        // (the tabs are <button>s). The close button stays click-only; a real
        // drag that began on a tab has its trailing click suppressed, so tabs
        // still switch on a clean click but a drag just moves the window.
        if (helpHeader) offs.push(makeFreelyDraggable(helpPanel, helpHeader, {
            onCommit: cancelMaximize,
            noDragSelector: '.help-close-btn, a, input, textarea, select, [data-no-drag]',
        }));
        if (helpResize) offs.push(makeResizableFromCorner(helpPanel, helpResize, { minWidth: 320, minHeight: 240, onCommit: cancelMaximize, onReset: cancelMaximize }));

        if (helpHeader) {
            const onDblClick = (e: MouseEvent): void => {
                // Ignore double-clicks on the close button or tab chips.
                if ((e.target as Element | null)?.closest('button, a, input, textarea, select')) return;
                e.preventDefault(); // suppress the default word-selection on the title
                const s = helpPanel.style;
                if (maximized && savedGeom) {
                    s.left = savedGeom.left; s.top = savedGeom.top;
                    s.right = savedGeom.right; s.bottom = savedGeom.bottom;
                    s.transform = savedGeom.transform;
                    s.width = savedGeom.width; s.height = savedGeom.height;
                    cancelMaximize();
                    return;
                }
                // Snapshot the current inline geometry (empty strings restore the
                // CSS-centered default), then fill the viewport bar a small margin.
                savedGeom = { left: s.left, top: s.top, right: s.right, bottom: s.bottom, transform: s.transform, width: s.width, height: s.height };
                s.left = `${MAXIMIZE_MARGIN_PX}px`;
                s.top = `${MAXIMIZE_MARGIN_PX}px`;
                s.right = 'auto';
                s.bottom = 'auto';
                s.transform = 'none';
                s.width = `calc(100vw - ${MAXIMIZE_MARGIN_PX * 2}px)`;
                s.height = `calc(100vh - ${MAXIMIZE_MARGIN_PX * 2}px)`;
                maximized = true;
                helpPanel.classList.add('maximized');
            };
            helpHeader.addEventListener('dblclick', onDblClick);
            offs.push(() => helpHeader.removeEventListener('dblclick', onDblClick));
        }
        // Browser-resize clamp: once the user has dragged the modal off the
        // CSS-centered position, shrinking the window otherwise leaves it
        // potentially off-screen. The clamp pulls it back so ≥80px of the
        // header stays grippable.
        if (helpHeader) offs.push(installFreeResizeClamp(helpPanel, helpHeader));

        // Raise the help to the top of the dynamic stack each time it is
        // opened (the `show` class is added). Covers the keyboard toggle,
        // the "Help" action, the helpStart path in dom.ts, and
        // the initial HTML state (the observer is set up after first paint
        // but we raise once below to catch that case). After the open, the
        // help still participates in normal pane-vs-help promotion — click
        // a pane and it goes back above the help, as expected.
        if (helpPanel.classList.contains('show')) raisePane(helpPanel);
        const showObserver = new MutationObserver((mutations) => {
            for (const m of mutations) {
                if (m.type === 'attributes' && m.attributeName === 'class'
                    && helpPanel.classList.contains('show')) {
                    raisePane(helpPanel);
                    return;
                }
            }
        });
        showObserver.observe(helpPanel, { attributes: true, attributeFilter: ['class'] });
        offs.push(() => showObserver.disconnect());

        const onClose = (): void => { helpPanel.classList.remove('show'); };
        helpClose.addEventListener('click', onClose);
        offs.push(() => helpClose.removeEventListener('click', onClose));

        const onKeyDown = (e: KeyboardEvent): void => {
            if (e.key === 'Escape') helpPanel.classList.remove('show');
            if (e.key === '?' || e.key === '/') {
                const tag = (document.activeElement && document.activeElement.tagName) || '';
                if (tag !== 'INPUT' && tag !== 'TEXTAREA') helpPanel.classList.toggle('show');
            }
        };
        document.addEventListener('keydown', onKeyDown);
        offs.push(() => document.removeEventListener('keydown', onKeyDown));
    }

    // Console panel mechanics (drag/resize/close/persist) are now owned by
    // the WindowRegistry — see initConsole() in ./console, which registers
    // 'console-panel' with the registry installed in initUI.

    return () => { for (const off of offs) { try { off(); } catch { /* swallow */ } } };
}
